interface MapFeatureColors {
  land: string;
  water: string;
  majorRoad: string;
  minorRoad: string;
  park: string;
  building: string;
  landcover: string;
}

/**
 * Derive tonal map feature colors from a single mapColor.
 * Produces a monochrome-ish relief look, NOT a colorful map.
 */
export function deriveMapColors(mapColor: string): MapFeatureColors {
  const rgb = hexToRgb(mapColor);
  const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  const isDark = brightness < 80;

  if (isDark) {
    return {
      land: mapColor,
      water: adjustColor(rgb, { r: -10, g: -10, b: 5 }),
      majorRoad: adjustBrightness(rgb, 0.12),
      minorRoad: adjustBrightness(rgb, 0.06),
      park: adjustColor(rgb, { r: -5, g: 8, b: -5 }),
      building: adjustBrightness(rgb, 0.07),
      landcover: adjustColor(rgb, { r: -3, g: 5, b: -3 }),
    };
  }

  return {
    land: mapColor,
    water: adjustColor(rgb, { r: -15, g: -10, b: 5 }),
    majorRoad: adjustBrightness(rgb, 0.10),
    minorRoad: adjustBrightness(rgb, 0.05),
    park: adjustColor(rgb, { r: -10, g: 5, b: -10 }),
    building: adjustBrightness(rgb, -0.04),
    landcover: adjustColor(rgb, { r: -5, g: 3, b: -5 }),
  };
}

/**
 * Apply monochrome tonal colors to ALL map layers.
 * Iterates every layer in the Mapbox style and recolors based on
 * layer type, source-layer, and ID to achieve a fully tonal look.
 *
 * @param map - A Mapbox GL map instance
 * @param mapColor - The palette's base map color (e.g. '#B5C9B5' for Sage)
 */
export function applyMonochromeTheme(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Mapbox GL Map duck-typed to avoid importing full types
  map: { getStyle(): { layers?: any[] }; setPaintProperty(id: string, prop: string, val: any): void },
  mapColor: string,
): void {
  const derived = deriveMapColors(mapColor);
  const style = map.getStyle();
  if (!style?.layers) return;

  // Custom layers we manage separately (3D buildings, etc.)
  const customLayerIds = new Set([
    '3d-buildings', 'buildings-fill', 'building-footprints', 'block-pattern',
  ]);

  for (const layer of style.layers) {
    if (customLayerIds.has(layer.id)) continue;

    const id: string = layer.id;
    const sl: string = layer['source-layer'] ?? '';

    try {
      switch (layer.type) {
        case 'background':
          map.setPaintProperty(id, 'background-color', derived.land);
          break;

        case 'fill': {
          // Determine fill color based on feature classification
          let color = derived.land; // default for unknown fill layers
          if (sl === 'water' || id.includes('water')) {
            color = derived.water;
          } else if (id.includes('park') || id.includes('garden') || id.includes('cemetery') || id === 'national-park') {
            color = derived.park;
          } else if (sl === 'landcover' || id.includes('landcover')) {
            color = derived.landcover;
          } else if (sl === 'building' || id.includes('building')) {
            color = derived.building;
          }
          // landuse (non-park), aeroway, land, etc. all get `derived.land`
          map.setPaintProperty(id, 'fill-color', color);
          break;
        }

        case 'line': {
          let color = derived.minorRoad; // default for line layers
          if (sl === 'waterway' || id.includes('waterway')) {
            color = derived.water;
          } else if (sl === 'road' || id.includes('road') || id.includes('bridge') || id.includes('tunnel')) {
            // Road casings (outline behind the road line) get land color
            // so roads appear as grooves/channels in the surface
            if (id.includes('-case')) {
              color = derived.land;
            } else {
              const isMajor = id.includes('motorway') || id.includes('trunk') ||
                id.includes('primary') || id.includes('secondary');
              color = isMajor ? derived.majorRoad : derived.minorRoad;
            }
          } else if (id.includes('admin') || id.includes('boundary')) {
            color = derived.land; // hide admin boundaries in tonal look
          }
          map.setPaintProperty(id, 'line-color', color);
          break;
        }

        case 'fill-extrusion':
          map.setPaintProperty(id, 'fill-extrusion-color', derived.building);
          break;
      }
    } catch {
      // Some layers may not support certain paint properties — skip silently
    }
  }
}

// Legacy mapping kept for reference (replaced by applyMonochromeTheme)
export const MAPBOX_LAYER_MAPPING = {
  water: ['water'],
  waterway: ['waterway'],
  majorRoad: ['road-primary', 'road-secondary-tertiary', 'road-motorway-trunk'],
  minorRoad: ['road-street', 'road-minor', 'road-construction'],
  park: ['landuse-park', 'landuse-cemetery', 'landuse-garden'],
  building: ['building'],
  background: ['background'],
} as const;

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  } : { r: 0, g: 0, b: 0 };
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  return '#' + [clamp(r), clamp(g), clamp(b)]
    .map(n => n.toString(16).padStart(2, '0')).join('');
}

function adjustBrightness(rgb: { r: number; g: number; b: number }, factor: number): string {
  const adjustment = 255 * factor;
  return rgbToHex(rgb.r + adjustment, rgb.g + adjustment, rgb.b + adjustment);
}

function adjustColor(
  rgb: { r: number; g: number; b: number },
  shift: { r: number; g: number; b: number }
): string {
  return rgbToHex(rgb.r + shift.r, rgb.g + shift.g, rgb.b + shift.b);
}
